import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import rivyouLogo from "../assets/Rivyou_idjI_O9kJC_1.svg";
import { openCookiePreferences } from "../lib/consent";
import SocialBrandIcon from "./SocialBrandIcon";
import ContactModal from "./ContactModal";
import { COMPANY } from "../data/company";

export default function Footer({ onNavigate }) {
  const navigate = useNavigate();
  const [contactOpen, setContactOpen] = useState(false);

  const handleLink = (pathOrTab) => {
    if (pathOrTab.startsWith("/")) {
      navigate(pathOrTab);
      if (!pathOrTab.includes("#")) window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (onNavigate) {
      onNavigate(pathOrTab);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <footer className="w-full bg-brand-obsidian text-[#edf5f0] pt-14 pb-12 border-t border-white/10 font-sans">
      <div className="consumer-footer-inner w-full max-w-[1280px] mx-auto px-4 sm:px-6">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-12 pb-12">
          {/* Brand Info & Socials */}
          <div className="lg:col-span-2 flex flex-col items-start gap-4">
            <div
              className="cursor-pointer transition-opacity hover:opacity-85"
              onClick={() => handleLink("home")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && handleLink("home")}
              title="Rivyou Home"
            >
              <img src={rivyouLogo} alt="Rivyou" width="91" height="32" className="h-8 w-auto" />
            </div>
            <p className="text-sm leading-relaxed text-[#94a3b8] max-w-sm m-0">
              Discover authentic reviews and ratings for India’s top D2C brands, verified directly with real checkout
              receipts and zero marketing bias.
            </p>

            {/* Official Social Media Badges */}
            <div className="flex items-center gap-2.5 pt-2">
              <a
                href="https://x.com/rivyou"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-white hover:bg-slate-100 rounded-full flex items-center justify-center transition-all border border-white/20 hover:border-white/50"
                aria-label="X (formerly Twitter)"
                title="Follow us on X"
              >
                <SocialBrandIcon logo platform="x" className="w-4 h-4" />
              </a>
              <a
                href="https://www.linkedin.com/company/rivyou"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-white hover:bg-slate-100 rounded-full flex items-center justify-center transition-all border border-white/20 hover:border-white/50"
                aria-label="LinkedIn"
                title="Connect on LinkedIn"
              >
                <SocialBrandIcon logo platform="linkedin" className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/rivyou.official/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-white hover:bg-slate-100 rounded-full flex items-center justify-center transition-all border border-white/20 hover:border-white/50"
                aria-label="Instagram"
                title="Follow on Instagram"
              >
                <SocialBrandIcon logo platform="instagram" className="w-4 h-4" />
              </a>
              <a
                href="https://www.facebook.com/people/Rivyou/61584136863602/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-white hover:bg-slate-100 rounded-full flex items-center justify-center transition-all border border-white/20 hover:border-white/50"
                aria-label="Facebook"
                title="Follow on Facebook"
              >
                <SocialBrandIcon logo platform="facebook" className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Column 1: Explore */}
          <div className="lg:col-span-1">
            <h2 className="font-heading text-xs font-bold tracking-wider text-white uppercase mb-4">Explore</h2>
            <ul className="flex flex-col gap-2.5 list-none p-0 m-0 text-sm text-[#94a3b8]">
              <li>
                <a
                  href="/"
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
                    event.preventDefault();
                    handleLink("home");
                  }}
                  className="text-inherit no-underline hover:text-brand-emerald transition-colors"
                >
                  Home
                </a>
              </li>
              <li>
                <a
                  href="/brands"
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
                    event.preventDefault();
                    handleLink("brands");
                  }}
                  className="text-inherit no-underline hover:text-brand-emerald transition-colors"
                >
                  Brands Directory
                </a>
              </li>
              <li>
                <a
                  href="/trust-issue"
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
                    event.preventDefault();
                    handleLink("trust-issue");
                  }}
                  className="text-inherit no-underline hover:text-brand-emerald transition-colors"
                >
                  Why Trust Us
                </a>
              </li>
              <li>
                <a
                  href="/the-badge/things-to-know"
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
                    event.preventDefault();
                    handleLink("/the-badge/things-to-know");
                  }}
                  className="text-inherit no-underline hover:text-brand-emerald transition-colors"
                >
                  The Badge
                </a>
              </li>
              <li>
                <a
                  href="/business"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => {
                    // Phones: go to the business site in the same tab; larger screens keep opening a new tab.
                    if (window.matchMedia?.("(max-width: 768px)").matches) {
                      event.preventDefault();
                      handleLink("/business");
                    }
                  }}
                  className="text-inherit hover:text-brand-emerald transition-colors no-underline"
                  aria-label="For Business"
                >
                  For Business
                </a>
              </li>
              <li>
                <a href="/app/" className="text-inherit hover:text-brand-emerald transition-colors no-underline">
                  Rivyou Consumer App
                </a>
              </li>
            </ul>
          </div>

          {/* Column 2: Company & Team */}
          <div className="lg:col-span-1">
            <h2 className="font-heading text-xs font-bold tracking-wider text-white uppercase mb-4">Company</h2>
            <ul className="flex flex-col gap-2.5 list-none p-0 m-0 text-sm text-[#94a3b8]">
              <li>
                <a
                  href="/about"
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
                    event.preventDefault();
                    handleLink("/about");
                  }}
                  className="text-inherit no-underline hover:text-brand-emerald transition-colors inline-flex items-center gap-1.5"
                >
                  <span>About Rivyou</span>
                </a>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => setContactOpen(true)}
                  className="bg-transparent border-0 p-0 text-inherit cursor-pointer text-left hover:text-brand-emerald transition-colors inline-flex items-center gap-1.5"
                >
                  <span>Contact Us</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3: Legal & Trust */}
          <div className="lg:col-span-1">
            <h2 className="font-heading text-xs font-bold tracking-wider text-white uppercase mb-4">Legal</h2>
            <ul className="flex flex-col gap-2.5 list-none p-0 m-0 text-sm text-[#94a3b8]">
              <li>
                <a
                  href="/privacy"
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
                    event.preventDefault();
                    handleLink("/privacy");
                  }}
                  className="text-inherit no-underline hover:text-brand-emerald transition-colors"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="/terms"
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
                    event.preventDefault();
                    handleLink("/terms");
                  }}
                  className="text-inherit no-underline hover:text-brand-emerald transition-colors"
                >
                  Terms & Conditions
                </a>
              </li>
              <li>
                <button
                  type="button"
                  onClick={openCookiePreferences}
                  className="bg-transparent border-0 p-0 text-inherit cursor-pointer text-left hover:text-brand-emerald transition-colors"
                >
                  Cookie Preferences
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal line */}
        <div className="border-t border-white/10 pt-6 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between text-xs text-[#94a3b8]">
          <p className="m-0">
            &copy; {new Date().getFullYear()} {COMPANY.productName}. A product of {COMPANY.legalName}, Bengaluru, India.{" "}
            <span aria-hidden="true">🇮🇳</span>
          </p>
        </div>
      </div>
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </footer>
  );
}
